import pointCosts from "./pointCostRules.json"
import {
  VIDEO_CHANNEL_OPTIONS,
  VIDEO_MODELS,
  type VideoModelPrice,
} from "../video/videoModels"

export type VideoGenerationPointCostParams = {
  duration: number
  model: API.Task.CreateVideoChannel
  referenceImageCount: number
  resolution: API.VideoResolution
}

export type VideoGenerationPointSavingOptions = {
  canChangeModel: boolean
  canChangeResolution: boolean
  canShortenDuration: boolean
}

const getVideoGenerationPointCostGroup = ({
  model,
  referenceImageCount,
  resolution,
}: Omit<VideoGenerationPointCostParams, "duration">) => {
  const pricing: VideoModelPrice[] = VIDEO_MODELS[model]?.pricing ?? []
  const groups = pricing.filter((item) => {
    return item.resolution === resolution
  })

  return (
    groups.find((item) => {
      return !item.maxImageCount || referenceImageCount <= item.maxImageCount
    }) ?? groups.at(-1)
  )
}

export const getExactVideoGenerationPointCost = ({
  duration,
  model,
  referenceImageCount,
  resolution,
}: VideoGenerationPointCostParams) => {
  const group = getVideoGenerationPointCostGroup({
    model,
    referenceImageCount,
    resolution,
  })

  return group?.items.find((item) => item.duration === duration)?.points
}

export const MIN_VIDEO_GENERATION_POINT_COST = Math.min(
  ...VIDEO_CHANNEL_OPTIONS.flatMap(({ pricing }) =>
    pricing.flatMap((group) => group.items.map((item) => item.points)),
  ),
)

type VideoEnhancementPointCostParams = {
  duration?: number
  enhanceType: API.Task.EnhanceVideoType
  resolution: API.Task.EnhanceVideoResolution
}

export const getWhiteBgImagePointCost = () => {
  return pointCosts.imageTasks.whiteBgImage.points
}

export const getModelFigureArtlinePointCost = () => {
  return pointCosts.imageTasks.modelFigureArtline.points
}

export const getFrameCopyFrameImgPointCost = () => {
  return pointCosts.frameCopyFrameImg.points
}

export const getVideoAnalysisPointCost = (durationSeconds?: number) => {
  if (
    typeof durationSeconds !== "number" ||
    !Number.isFinite(durationSeconds)
  ) {
    return pointCosts.videoAnalysis[0]?.points || 0
  }

  const match = pointCosts.videoAnalysis.find((item) => {
    const minDuration = item.minDurationSeconds || 0
    const maxDuration = item.maxDurationSeconds || Number.POSITIVE_INFINITY

    return durationSeconds > minDuration && durationSeconds <= maxDuration
  })

  return match?.points || pointCosts.videoAnalysis.at(-1)?.points || 0
}

export const getFrameCopyScriptPointCost = () => {
  return pointCosts.frameCopyScript.points
}

export const getVideoEnhancementPointCost = (
  params: VideoEnhancementPointCostParams,
) => {
  const { duration, enhanceType, resolution } = params
  const rule = pointCosts.videoEnhancement.items.find((item) => {
    return item.enhanceType === enhanceType && item.resolution === resolution
  })

  if (!rule) return 0

  const billingDuration =
    typeof duration === "number" && Number.isFinite(duration) && duration > 0
      ? Math.max(1, Math.floor(duration))
      : 1
  const blockCount = Math.ceil(
    billingDuration / pointCosts.videoEnhancement.blockDurationSeconds,
  )

  return rule.pointsPerBlock * blockCount
}

export const getVideoSubtitleErasureBillingUnits = (
  durationSeconds?: number,
) => {
  if (
    typeof durationSeconds !== "number" ||
    !Number.isFinite(durationSeconds) ||
    durationSeconds <= 0
  ) {
    return 0
  }

  return Math.ceil(
    durationSeconds / pointCosts.videoSubtitleErasure.blockDurationSeconds,
  )
}

export const getVideoSubtitleErasurePointCost = (durationSeconds?: number) => {
  return (
    getVideoSubtitleErasureBillingUnits(durationSeconds) *
    pointCosts.videoSubtitleErasure.pointsPerBlock
  )
}

export const getVideoGenerationPointCost = ({
  duration,
  model,
  referenceImageCount,
  resolution,
}: VideoGenerationPointCostParams) => {
  const group = getVideoGenerationPointCostGroup({
    model,
    referenceImageCount,
    resolution,
  })

  return (
    group?.items.find((item) => item.duration === duration)?.points ||
    group?.items.at(-1)?.points ||
    0
  )
}

export const getVideoGenerationPointSavingOptions = (
  options: VideoGenerationPointCostParams,
): VideoGenerationPointSavingOptions | undefined => {
  const currentCost = getExactVideoGenerationPointCost(options)

  if (typeof currentCost !== "number") return undefined

  const { duration, model, referenceImageCount, resolution } = options
  const currentResolution = Number.parseInt(resolution, 10)
  const canChangeModel = VIDEO_CHANNEL_OPTIONS.some(({ value, pricing }) => {
    if (
      value === model ||
      !pricing.some((group) => group.resolution === resolution)
    )
      return false

    const cost = getExactVideoGenerationPointCost({
      duration,
      model: value,
      referenceImageCount,
      resolution,
    })

    return typeof cost === "number" && cost < currentCost
  })
  const canChangeResolution = VIDEO_MODELS[model].pricing.some((group) => {
    const resolutionValue = Number.parseInt(group.resolution, 10)
    const cost = getExactVideoGenerationPointCost({
      duration,
      model,
      referenceImageCount,
      resolution: group.resolution,
    })

    return (
      resolutionValue < currentResolution &&
      typeof cost === "number" &&
      cost < currentCost
    )
  })
  const currentGroup = getVideoGenerationPointCostGroup({
    model,
    referenceImageCount,
    resolution,
  })
  const canShortenDuration = Boolean(
    currentGroup?.items.some((item) => {
      return item.duration < duration && item.points < currentCost
    }),
  )

  return {
    canChangeModel,
    canChangeResolution,
    canShortenDuration,
  }
}
